import React, { useState, useEffect } from 'react'
import config from '../../config.js'
import { groupColor } from './theme'
import { formatSeconds, formatInches, formatWeight, formatCurrency, formatPct } from './formatters'
import NesterMaterialsBill from './NesterMaterialsBill'

const NESTER = config.NESTER_API_URL

/**
 * The Overview tab — a dashboard showing all-project rollups plus a card
 * per material group with thumbnail, stats, time/cost bars, and part
 * thumbnails. Click a card → onNavigateToGroup(groupId).
 *
 * Everything here is already available in the nest result payload; this
 * panel reorganises it into a scan-and-act layout.
 */
export default function NesterDashboard({
  colors,
  nestResult,
  materialGroups = [],
  parts = [],
  onNavigateToGroup,
  onJumpToCoach,
  coachSuggestionCount,
}) {
  const isMulti = nestResult?.group_results?.length > 0
  const groups = isMulti ? nestResult.group_results : (nestResult ? [nestResult] : [])
  const totals = aggregate(groups)

  if (!nestResult) {
    return (
      <div style={styles.empty(colors)}>
        <div style={{ fontSize: 40, opacity: 0.3 }}>◇</div>
        <div style={{ fontSize: 14, color: colors.textMuted, marginTop: 8 }}>
          No nest generated yet. Upload parts and hit <strong>▶ Nest</strong> to see results here.
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '14px 16px' }}>
      {/* ── Totals strip ─────────────────────────────────────────── */}
      <TotalsStrip colors={colors} totals={totals} />

      {/* ── Unplaced warning ─────────────────────────────────────── */}
      {nestResult.unplaced_parts?.length > 0 && (
        <div style={styles.unplaced(colors)}>
          <div style={{ fontSize: 13, fontWeight: 700, color: colors.danger, marginBottom: 4 }}>
            ⚠ {nestResult.unplaced_parts.length} part{nestResult.unplaced_parts.length > 1 ? 's' : ''} could not be placed
          </div>
          {nestResult.unplaced_parts.map((p, i) => (
            <div key={p.part_id + '-' + i} style={{ fontSize: 12, color: colors.text, marginTop: 2 }}>
              <strong>{p.part_label || p.file_name}</strong>
              {p.reason && <span style={{ color: colors.textMuted }}> — {p.reason}</span>}
            </div>
          ))}
        </div>
      )}

      {/* ── AI Coach nudge ───────────────────────────────────────── */}
      {coachSuggestionCount > 0 && (
        <button onClick={onJumpToCoach} style={styles.coachNudge(colors)}>
          <span style={{ fontSize: 16 }}>🧠</span>
          <span><strong>{coachSuggestionCount}</strong> AI Coach suggestion{coachSuggestionCount !== 1 ? 's' : ''} — review to improve this nest</span>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: colors.textMuted }}>View →</span>
        </button>
      )}

      {/* ── Per-group cards ─────────────────────────────────────── */}
      <div style={styles.grid}>
        {groups.map((g, i) => {
          const mg = materialGroups?.find(m => m.group_id === g.material_group_id)
          const accent = groupColor(i)
          const tabId = isMulti
            ? `group-${g.material_group_id || `idx-${i}`}`
            : 'group-single'
          return (
            <GroupCard key={g.nest_id || i}
              colors={colors}
              group={g}
              mg={mg}
              idx={i}
              accent={accent}
              parts={parts.filter(p => !g.material_group_id || p.material_group_id === g.material_group_id)}
              onClick={() => onNavigateToGroup?.(tabId)}
            />
          )
        })}
      </div>

      {/* ── Material Requirements + Parts Manifest ──────────────── */}
      <NesterMaterialsBill
        colors={colors}
        nestResult={nestResult}
        materialGroups={materialGroups}
      />
    </div>
  )
}

/* ── Totals strip ─────────────────────────────────────────────── */

function TotalsStrip({ colors, totals }) {
  const cells = [
    { label: 'Total Sheets', value: totals.sheets, accent: true },
    { label: 'Total Time', value: formatSeconds(totals.time), accent: true },
    { label: 'Total Cost', value: formatCurrency(totals.cost), accent: true, color: colors.accent },
    { label: 'Avg Util', value: formatPct(totals.avgUtil) },
    { label: 'Parts', value: totals.parts },
    { label: 'Pierces', value: totals.pierces },
    { label: 'Cut Length', value: formatInches(totals.cutLength, 0) },
    { label: '$/Part', value: totals.parts > 0 && totals.cost > 0 ? formatCurrency(totals.cost / totals.parts) : '—' },
  ]
  return (
    <div style={styles.totalsWrap(colors)}>
      {cells.map(c => (
        <div key={c.label} style={{
          ...styles.totalCell(colors),
          ...(c.accent ? styles.totalCellAccent(colors) : {}),
        }}>
          <div style={styles.totalLabel(colors)}>{c.label}</div>
          <div style={{
            ...styles.totalValue(colors),
            ...(c.color ? { color: c.color } : {}),
          }}>{c.value}</div>
        </div>
      ))}
    </div>
  )
}

/* ── Per-group card ─────────────────────────────────────────────── */

function GroupCard({ colors, group, mg, idx, accent, parts, onClick }) {
  const de = group.detailed_estimate
  const cs = group.cost_summary
  const totalSec = de?.total_sec || group.est_total_time_sec || 0
  const timeParts = buildTimeBar(de)
  const costParts = buildCostBar(cs)

  const title = mg?.group_name
    || group.material_group_name
    || [mg?.material_type, mg?.thickness_display].filter(Boolean).join(' ')
    || `Group ${idx + 1}`

  const spec = [
    mg?.material_type,
    mg?.thickness_display,
    mg?.surface_finish,
    mg?.sheet_width && mg?.sheet_length ? `${mg.sheet_width}×${mg.sheet_length}"` : null,
    mg?.gas_type,
  ].filter(Boolean).join(' · ')

  const sheetPartCount = parts.length
  const thumbParts = parts.filter(p => p.svg_data).slice(0, 8)

  return (
    <div onClick={onClick} style={styles.card(colors, accent)}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 12px',
        borderBottom: `1px solid ${colors.border}`,
        background: colors.panelAlt,
      }}>
        <span style={{
          width: 12, height: 12, borderRadius: '50%',
          background: accent, flexShrink: 0,
        }} />
        <div style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>{title}</div>
        {group.sheets_needed > 0 && (
          <span style={{
            fontSize: 11, fontWeight: 600,
            padding: '2px 8px', borderRadius: 10,
            background: accent, color: '#fff',
            marginLeft: 'auto',
          }}>
            {group.sheets_needed} sheet{group.sheets_needed === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {spec && (
        <div style={{ padding: '6px 12px', fontSize: 11, color: colors.textMuted }}>
          {spec}
        </div>
      )}

      {/* Thumbnail */}
      <ThumbnailSvg nestId={group.nest_id} colors={colors} />

      {/* Stat grid */}
      <div style={styles.statGrid(colors)}>
        <Stat c={colors} label="Util" value={formatPct(group.utilization_pct)} />
        <Stat c={colors} label="Time" value={formatSeconds(totalSec)} />
        <Stat c={colors} label="Cost" value={formatCurrency(cs?.total_cost)} accent />
        <Stat c={colors} label="Pierces" value={group.total_pierce_count ?? '—'} />
        <Stat c={colors} label="Cut" value={formatInches(de?.total_path_length_in ?? group.total_cut_length_in, 0)} />
        <Stat c={colors} label="Weight" value={formatWeight(cs?.raw_sheet_weight_lbs)} />
        <Stat c={colors} label="Scrap" value={formatPct(cs?.scrap_pct)} />
        <Stat c={colors} label="$/Part" value={formatCurrency(cs?.cost_per_part)} />
      </div>

      {/* Time / cost bars */}
      <div style={{ padding: '0 12px 10px' }}>
        <MiniBar label="Time" colors={colors} segments={timeParts} />
        <MiniBar label="Cost" colors={colors} segments={costParts} />
      </div>

      {/* Parts preview */}
      <div style={styles.partStrip(colors)}>
        <span style={{ fontSize: 11, color: colors.textMuted }}>
          {sheetPartCount} part{sheetPartCount === 1 ? '' : 's'}
        </span>
        {thumbParts.map(p => (
          <div key={p.part_id}
            title={p.part_label || p.file_name}
            style={styles.partThumb(colors)}
            dangerouslySetInnerHTML={{ __html: p.svg_data }} />
        ))}
        {sheetPartCount > thumbParts.length && (
          <span style={{ fontSize: 11, color: colors.textMuted, marginLeft: 4 }}>
            +{sheetPartCount - thumbParts.length}
          </span>
        )}
      </div>

      <div style={styles.cardFooter(colors, accent)}>
        View detail →
      </div>
    </div>
  )
}

/* ── Thumbnail loader ───────────────────────────────────────────── */

function ThumbnailSvg({ nestId, colors }) {
  const [svg, setSvg] = useState(null)
  useEffect(() => {
    if (!nestId) { setSvg(''); return }
    let cancelled = false
    fetch(`${NESTER}/nests/${nestId}/svg`)
      .then(r => r.ok ? r.text() : '')
      .then(text => { if (!cancelled) setSvg(text || '') })
      .catch(() => { if (!cancelled) setSvg('') })
    return () => { cancelled = true }
  }, [nestId])

  return (
    <div style={{
      padding: 8,
      background: colors.panelAlt,
      borderBottom: `1px solid ${colors.border}`,
      height: 160,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      overflow: 'hidden',
    }}>
      {svg === null && <div style={{ fontSize: 11, color: colors.textMuted }}>Loading…</div>}
      {svg === '' && <div style={{ fontSize: 11, color: colors.warn }}>No layout</div>}
      {svg && (
        <div style={{
          maxWidth: '100%', maxHeight: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
          dangerouslySetInnerHTML={{ __html: svg }} />
      )}
    </div>
  )
}

/* ── Mini bar ───────────────────────────────────────────────────── */

function MiniBar({ label, colors, segments }) {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0)
  if (total <= 0) return null
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: colors.textMuted, marginBottom: 3 }}>
        <span>{label}</span>
      </div>
      <div style={{
        display: 'flex', height: 10, borderRadius: 2, overflow: 'hidden',
        border: `1px solid ${colors.border}`,
      }}>
        {segments.filter(s => s.value > 0).map(s => (
          <div key={s.key}
            title={`${s.label}: ${((s.value / total) * 100).toFixed(1)}%`}
            style={{
              width: `${(s.value / total) * 100}%`,
              background: s.color,
              minWidth: 2,
            }} />
        ))}
      </div>
    </div>
  )
}

function Stat({ c, label, value, accent }) {
  return (
    <div style={{
      padding: '6px 8px',
      border: `1px solid ${c.border}`, borderRadius: 4,
      background: accent ? c.accentSoft : c.panel,
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 9, color: c.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
        {label}
      </div>
      <div style={{
        fontSize: 13, fontWeight: 700, marginTop: 1,
        color: accent ? c.accent : c.text,
      }}>
        {value}
      </div>
    </div>
  )
}

/* ── Helpers ────────────────────────────────────────────────────── */

function aggregate(groups) {
  let sheets = 0, time = 0, cost = 0, parts = 0, cutLength = 0, pierces = 0, utilSum = 0, utilCount = 0
  for (const g of groups) {
    sheets += g.sheets_needed || 0
    time += g.detailed_estimate?.total_sec || g.est_total_time_sec || 0
    cost += g.cost_summary?.total_cost || 0
    cutLength += g.detailed_estimate?.total_path_length_in || g.total_cut_length_in || 0
    pierces += g.total_pierce_count || 0
    if (g.utilization_pct != null) { utilSum += g.utilization_pct; utilCount++ }
    if (g.parts_summary) parts += g.parts_summary.reduce((s, p) => s + (p.quantity || 0), 0)
  }
  return {
    sheets, time, cost, parts, cutLength, pierces,
    avgUtil: utilCount > 0 ? utilSum / utilCount : null,
  }
}

function buildTimeBar(de) {
  if (!de) return []
  return [
    { key: 'cut', label: 'Cut', value: de.total_cut_path_sec, color: '#2563eb' },
    { key: 'sh',  label: 'SH',  value: de.total_sh_cut_sec, color: '#7c3aed' },
    { key: 'mv',  label: 'Move', value: de.total_move_sec, color: '#059669' },
    { key: 'pi',  label: 'Pierce', value: de.total_pierce_sec, color: '#dc2626' },
    { key: 'nc',  label: 'Nozzle', value: de.total_nozzle_clean_sec, color: '#f59e0b' },
    { key: 'al',  label: 'Align', value: de.total_alignment_sec, color: '#8b5cf6' },
    { key: 'sh2', label: 'Shuttle', value: de.total_reposition_sec, color: '#d97706' },
    { key: 'ot',  label: 'Other', value: de.total_other_sec, color: '#6b7280' },
  ]
}

function buildCostBar(cs) {
  if (!cs) return []
  return [
    { key: 'mat', label: 'Material', value: cs.material_cost || 0, color: '#2563eb' },
    { key: 'gas', label: 'Gas', value: cs.gas_cost || 0, color: '#d97706' },
    { key: 'con', label: 'Consumable', value: cs.consumable_cost || 0, color: '#7c3aed' },
  ]
}

/* ── Styles ─────────────────────────────────────────────────────── */

const styles = {
  empty: (c) => ({
    padding: '60px 20px', textAlign: 'center',
    color: c.textMuted,
  }),
  totalsWrap: (c) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
    gap: 8,
    marginBottom: 14,
  }),
  totalCell: (c) => ({
    padding: '10px 12px',
    background: c.panel,
    border: `1px solid ${c.border}`,
    borderRadius: 6,
    textAlign: 'center',
  }),
  totalCellAccent: (c) => ({
    background: c.accentSoft,
    border: `1px solid ${c.accentBorder}`,
  }),
  totalLabel: (c) => ({
    fontSize: 10, fontWeight: 700,
    color: c.textMuted,
    textTransform: 'uppercase', letterSpacing: '0.06em',
  }),
  totalValue: (c) => ({
    fontSize: 18, fontWeight: 800,
    color: c.text, marginTop: 2,
  }),
  unplaced: (c) => ({
    padding: '10px 14px', marginBottom: 12,
    background: c.dangerSoft,
    border: `1px solid ${c.danger}`, borderRadius: 6,
  }),
  coachNudge: (c) => ({
    width: '100%',
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 14px', marginBottom: 12,
    background: c.accentSoft,
    border: `1px solid ${c.accentBorder}`,
    borderRadius: 6,
    color: c.text, fontSize: 13,
    cursor: 'pointer', textAlign: 'left',
  }),
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: 14,
  },
  card: (c, accent) => ({
    background: c.panel,
    border: `1px solid ${c.border}`,
    borderLeft: `4px solid ${accent}`,
    borderRadius: 8,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.15s, box-shadow 0.15s',
    boxShadow: c.shadow,
  }),
  statGrid: (c) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 4,
    padding: '8px 12px',
  }),
  partStrip: (c) => ({
    display: 'flex', alignItems: 'center', gap: 4,
    padding: '6px 12px',
    borderTop: `1px solid ${c.border}`,
    background: c.panelAlt,
    flexWrap: 'wrap',
  }),
  partThumb: (c) => ({
    width: 26, height: 26,
    border: `1px solid ${c.border}`, borderRadius: 3,
    background: c.panel,
    overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }),
  cardFooter: (c, accent) => ({
    padding: '8px 12px',
    textAlign: 'right',
    fontSize: 12, fontWeight: 600, color: accent,
    borderTop: `1px solid ${c.border}`,
  }),
}
